import { AUCTION_INCREMENT_ID_REGEX, DIRECT_SALE_INCREMENT_ID_REGEX, SEARCH_MIN_QUERY_LENGTH, vinValidator } from '@lib/shared';
import type { Ref } from 'vue';
import { isNavigationFailure } from 'vue-router';

import { apiSearchInterpret, AssetType, SellingMethod } from '~/apiClient';
import { type PlatformSellingMethod } from '~/types/sale.type';

// Turnstile tokens expire after 300 seconds; one older than this is replaced rather than risked.
const TURNSTILE_TOKEN_MAX_AGE_MS = 4 * 60 * 1000;

// Above the backend's own 10-second cap on the model: this one catches a captcha that never answers (script blocked)
// or a request that hangs, which would otherwise leave the visitor on a spinner.
const SEARCH_INTERPRET_TIMEOUT_MS = 12_000;

// Past the first model's usual answer (1.3 s at the median): beyond it the second model is most likely at work.
const SLOW_INTERPRETATION_MS = 3000;

/** Where an interpreted search stands: the panel tells the visitor, and `none` covers every other wait. */
export type QuickSearchInterpretPhase = 'none' | 'reading' | 'slow';

export type UseQuickSearchOptions = {
    onClose?: () => void;
};

export type UseQuickSearchReturn = {
    inputValue: Ref<string>;
    sellingMethod: Ref<PlatformSellingMethod>;
    isLoading: Ref<boolean>;
    interpretPhase: Ref<QuickSearchInterpretPhase>;
    turnstileContainerId: string;
    onSubmit: () => Promise<void>;
};

type PendingTurnstileToken = { promise: Promise<string | null>; requestedAt: number };

const isListingCodeOrVin = (text: string): boolean =>
    AUCTION_INCREMENT_ID_REGEX.test(text) || DIRECT_SALE_INCREMENT_ID_REGEX.test(text) || vinValidator(text);

const rejectOnAbort = (signal: AbortSignal): Promise<never> =>
    new Promise((_, reject) => signal.addEventListener('abort', () => reject(signal.reason), { once: true }));

export const useQuickSearch = (options: UseQuickSearchOptions = {}): UseQuickSearchReturn => {
    const localePath = useLocalePath();
    const route = useRoute();
    const { locale } = useTypedI18n();
    const { formatSlpRoute } = useSlpRouteUtils();
    const { aiSearchDisabled } = useRuntimeConfig().public;
    const { turnstileContainerId, getToken, formatRequestOptions } = useInteractionOnlyTurnstile('search', { lazy: true });
    const aiLanding = useQuickSearchAiLanding();

    // DATA
    const inputValue = ref('');
    const sellingMethod = ref<PlatformSellingMethod>(SellingMethod.DirectSale);
    const isLoading = ref(false);
    const interpretPhase = ref<QuickSearchInterpretPhase>('none');
    let isDisposed = false;

    // METHODS
    let pendingTurnstileToken: PendingTurnstileToken | null = null;

    const isFresh = (token: PendingTurnstileToken | null): token is PendingTurnstileToken =>
        !!token && Date.now() - token.requestedAt < TURNSTILE_TOKEN_MAX_AGE_MS;

    // Asked for as soon as the visitor types, so the challenge does not add to the wait after Enter.
    const prefetchTurnstileToken = (): void => {
        if (isFresh(pendingTurnstileToken)) return;

        const promise = getToken();

        // A failed challenge is no token to keep: the next search asks again.
        promise.catch(() => {
            if (pendingTurnstileToken?.promise === promise) pendingTurnstileToken = null;
        });
        pendingTurnstileToken = { promise, requestedAt: Date.now() };
    };

    // A token is single-use: whoever takes it leaves the next search to ask for a new one.
    const takeTurnstileToken = (): Promise<string | null> => {
        const promise = isFresh(pendingTurnstileToken) ? pendingTurnstileToken.promise : getToken();

        pendingTurnstileToken = null;

        return promise;
    };

    // Below the threshold the SLP would drop the `q` anyway: leave it out and open the whole listing.
    const getTextSearchRoute = (text: string, listing: FormatSlpRouteParams): ReturnType<typeof formatSlpRoute> =>
        formatSlpRoute(listing, text.length >= SEARCH_MIN_QUERY_LENGTH ? { [SLP_QUERY_KEYS.q]: text } : {});

    // Null on any failure (captcha, rate limit, model) and when nothing was read: the visitor then gets the text search.
    const getInterpretedRoute = async (text: string, listing: FormatSlpRouteParams): Promise<ReturnType<typeof formatSlpRoute> | null> => {
        const signal = AbortSignal.timeout(SEARCH_INTERPRET_TIMEOUT_MS);

        try {
            const token = await Promise.race([takeTurnstileToken(), rejectOnAbort(signal)]);
            const interpretation = await apiSearchInterpret(
                { q: text, sellingMethod: listing.sellingMethod, language: localeToLanguage(locale.value), urlPath: route.path },
                { ...formatRequestOptions(token), signal }
            );
            const interpretedRoute = getInterpretedSlpRoute(interpretation);

            if (!interpretedRoute) return null;

            const { platformClass, brandUrlKey, query } = interpretedRoute;

            return formatSlpRoute({ ...listing, platformClass, brandUrlKey }, query);
        } catch {
            return null;
        }
    };

    const interpret = async (text: string, listing: FormatSlpRouteParams): Promise<ReturnType<typeof formatSlpRoute> | null> => {
        interpretPhase.value = 'reading';

        const slowTimer = setTimeout(() => {
            interpretPhase.value = 'slow';
        }, SLOW_INTERPRETATION_MS);

        try {
            return await getInterpretedRoute(text, listing);
        } finally {
            clearTimeout(slowTimer);
        }
    };

    // True once the visitor is on the way to the listing, false when the navigation left them where they were.
    const goTo = async (target: ReturnType<typeof formatSlpRoute>): Promise<boolean> => {
        const failure = await navigateTo(localePath(target));

        // Also closes on a `duplicated` NavigationFailure, where leaving the panel open is a dead end.
        options.onClose?.();

        return !isNavigationFailure(failure);
    };

    const onSubmit = async (): Promise<void> => {
        if (isLoading.value) return;

        const trimmed = inputValue.value.trim();
        // Read at Enter: the tabs stay usable while the search waits, and the listing must be the one searched.
        const listing: FormatSlpRouteParams = { sellingMethod: sellingMethod.value, assetType: AssetType.Bus };
        const textSearchRoute = getTextSearchRoute(trimmed, listing);
        // Too short to search, a listing code or a VIN: nothing to interpret.
        const isTextSearch = aiSearchDisabled || trimmed.length < SEARCH_MIN_QUERY_LENGTH || isListingCodeOrVin(trimmed);
        let hasNavigated = false;

        isLoading.value = true;

        try {
            const interpretedRoute = isTextSearch ? null : await interpret(trimmed, listing);

            if (isDisposed) return;

            aiLanding.value = interpretedRoute ? 'armed' : null;
            hasNavigated = await goTo(interpretedRoute ?? textSearchRoute);
        } finally {
            // The navigation resolves before the listing has loaded, and the listing is what replaces the panel: going
            // idle in between would end the search ahead of its results.
            if (!hasNavigated) {
                isLoading.value = false;
                interpretPhase.value = 'none';
            }
        }
    };

    // WATCHERS
    // A listing code names its channel. Follow it, or Enter lands on an empty SLP: each listing only fast-paths its own
    // prefix. On every edit, not only when the channel changes: a code pasted over another of the same channel must still
    // beat a tab picked by hand.
    watch(
        () => inputValue.value.trim(),
        trimmed => {
            if (AUCTION_INCREMENT_ID_REGEX.test(trimmed)) sellingMethod.value = SellingMethod.Auction;
            else if (DIRECT_SALE_INCREMENT_ID_REGEX.test(trimmed)) sellingMethod.value = SellingMethod.DirectSale;

            if (!aiSearchDisabled && trimmed.length >= SEARCH_MIN_QUERY_LENGTH) prefetchTurnstileToken();
        }
    );

    // LIFECYCLE
    // Enter can wait seconds for the captcha and the model: a panel closed meanwhile (Esc in the modal, a link out of the
    // hero) must not navigate once they answer.
    tryOnScopeDispose(() => {
        isDisposed = true;
    });

    return {
        inputValue,
        sellingMethod,
        isLoading,
        interpretPhase,
        turnstileContainerId,
        onSubmit,
    };
};
